/** Manual stroke-phase labels on a review clip (replay, auto-eval, KoT, feedback). */

export const STROKE_SIDES = Object.freeze(["forehand", "backhand", "serve"]);

export const STROKE_SPINS = Object.freeze([
  "topspin",
  "drive",
  "block",
  "push",
  "smash",
  "chop",
  "lob",
]);

export const RALLY_PHASES = Object.freeze([
  "preparation",
  "forward_swing",
  "contact",
  "follow_through",
  "recovery",
]);

export const SERVE_PHASES = Object.freeze([
  "preparation",
  "toss",
  "swing",
  "contact",
  "recovery",
]);

export function phasesForSide(side) {
  return side === "serve" ? SERVE_PHASES : RALLY_PHASES;
}

export function newStrokeId() {
  return `sl_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
}

/** Accepts a stored range or a legacy single-frame stamp (`t_ms` = start). */
export function normalizePhase(raw) {
  if (!raw || typeof raw !== "object") return null;
  let start = Number(raw.t_start_ms ?? raw.t_ms);
  let end = Number(raw.t_end_ms);
  const phase = {};
  if (Number.isFinite(start)) phase.t_start_ms = Math.max(0, Math.round(start));
  if (Number.isFinite(end)) phase.t_end_ms = Math.max(0, Math.round(end));
  if (
    Number.isFinite(phase.t_start_ms) &&
    Number.isFinite(phase.t_end_ms) &&
    phase.t_end_ms < phase.t_start_ms
  ) {
    const swap = phase.t_start_ms;
    phase.t_start_ms = phase.t_end_ms;
    phase.t_end_ms = swap;
  }
  if (!Number.isFinite(phase.t_start_ms) && !Number.isFinite(phase.t_end_ms)) {
    return null;
  }
  return phase;
}

export function phaseDurationMs(raw) {
  const phase = normalizePhase(raw);
  if (
    !phase ||
    !Number.isFinite(phase.t_start_ms) ||
    !Number.isFinite(phase.t_end_ms)
  ) {
    return null;
  }
  return phase.t_end_ms - phase.t_start_ms;
}

/** Earliest In to latest Out across the stroke. */
export function strokeDurationMs(stroke) {
  let min = Infinity;
  let max = -Infinity;
  for (const raw of Object.values(stroke?.phases || {})) {
    const phase = normalizePhase(raw);
    if (!phase) continue;
    if (Number.isFinite(phase.t_start_ms)) min = Math.min(min, phase.t_start_ms);
    const end = Number.isFinite(phase.t_end_ms)
      ? phase.t_end_ms
      : phase.t_start_ms;
    if (Number.isFinite(end)) max = Math.max(max, end);
  }
  if (!Number.isFinite(min) || !Number.isFinite(max) || max < min) return null;
  return max - min;
}

/**
 * Stamp the current frame as the In or Out edge of `phase`.
 * Creates a stroke when none is selected.
 */
export function tagStrokePhase(strokes, { strokeId, phase, tMs, side, spin, edge = "start" }) {
  const list = (Array.isArray(strokes) ? strokes : []).map((stroke) => ({
    ...stroke,
    phases: { ...(stroke.phases || {}) },
  }));
  const resolvedSide = STROKE_SIDES.includes(side) ? side : "forehand";
  const resolvedSpin =
    resolvedSide === "serve"
      ? "serve"
      : STROKE_SPINS.includes(spin)
        ? spin
        : "topspin";
  const allowed = phasesForSide(resolvedSide);
  if (!allowed.includes(phase)) {
    return { strokes: list, activeId: strokeId || null };
  }
  const time = Math.max(0, Math.round(Number(tMs) || 0));
  let stroke = strokeId ? list.find((item) => item.id === strokeId) : null;
  if (!stroke) {
    stroke = {
      id: strokeId || newStrokeId(),
      side: resolvedSide,
      spin: resolvedSpin,
      phases: {},
    };
    list.push(stroke);
  }
  stroke.side = resolvedSide;
  stroke.spin = resolvedSpin;
  const nextPhases = {};
  for (const key of allowed) {
    const normalized = normalizePhase(stroke.phases?.[key]);
    if (normalized) nextPhases[key] = normalized;
  }
  const prev = nextPhases[phase] || {};
  if (edge === "end") {
    nextPhases[phase] = normalizePhase({
      t_start_ms: Number.isFinite(prev.t_start_ms) ? prev.t_start_ms : time,
      t_end_ms: time,
    });
  } else {
    nextPhases[phase] = normalizePhase({
      t_start_ms: time,
      t_end_ms: prev.t_end_ms,
    });
  }
  stroke.phases = nextPhases;
  return { strokes: list, activeId: stroke.id };
}

export function strokePhaseCount(stroke) {
  return Object.keys(stroke?.phases || {}).filter((key) =>
    normalizePhase(stroke.phases[key])
  ).length;
}

export function firstPhaseMs(stroke, side) {
  const order = phasesForSide(side || stroke?.side);
  for (const key of order) {
    const phase = normalizePhase(stroke?.phases?.[key]);
    if (phase && Number.isFinite(phase.t_start_ms)) return phase.t_start_ms;
    if (phase && Number.isFinite(phase.t_end_ms)) return phase.t_end_ms;
  }
  return null;
}

const STORAGE_PREFIX = "smarttt.strokeLabels.";

export function strokeLabelStorageKey(messageId, url) {
  const id = String(messageId || "").trim();
  if (id) return `${STORAGE_PREFIX}${id}`;
  const src = String(url || "").trim();
  if (!src) return null;
  return `${STORAGE_PREFIX}url:${src.slice(0, 180)}`;
}

export function loadStrokeLabels(key) {
  if (!key || typeof localStorage === "undefined") return [];
  try {
    const raw = JSON.parse(localStorage.getItem(key) || "null");
    return Array.isArray(raw?.strokes) ? raw.strokes : [];
  } catch {
    return [];
  }
}

export function saveStrokeLabels(key, strokes) {
  if (!key || typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(
      key,
      JSON.stringify({ strokes: Array.isArray(strokes) ? strokes : [] })
    );
  } catch {
    /* quota or private mode */
  }
}
